import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ConfirmDialog } from "@tabipla/components";
import { useConfirmDialog } from "@tabipla/hooks/confirm-dialog";
import type {
  SessionResponseDto,
  GroupMemberResponseDto,
  ExpenseResponseDto,
} from "@tabipla/shared";
import {
  useExpenses,
  useBalances,
  useCreateExpense,
  useUpdateExpense,
  useDeleteExpense,
  useSplitExpenseEqually,
} from "../../../application/hooks/useExpenses";
import {
  useCompleteSession,
  useReopenSession,
  useStartSettling,
  useSessionImages,
  useAddSessionImage,
  useRemoveSessionImage,
} from "../../../application/hooks/useSessions";
import { SessionDetailPresenter } from "./SessionDetailPresenter";

interface SessionDetailContainerProps {
  groupId: string;
  session: SessionResponseDto;
  members: GroupMemberResponseDto[];
  currentUserId: string;
  currentUserType: "user" | "guest";
  onBack?: () => void;
  canEdit: boolean;
  /** Hide session navigation UI for simplified group detail view */
  simplified?: boolean;
}

/**
 * SessionDetailContainer - handles all data fetching, mutations, and state management.
 * Follows Container/Presenter pattern for better testability and separation of concerns.
 */
export function SessionDetailContainer({
  groupId,
  session,
  members,
  currentUserId,
  currentUserType,
  onBack,
  canEdit,
  simplified = false,
}: SessionDetailContainerProps) {
  const { t } = useTranslation();
  const { confirm, dialogProps } = useConfirmDialog();

  // ==================== UI State ====================
  const [showExpenseDialog, setShowExpenseDialog] = useState(false);
  const [editingExpense, setEditingExpense] = useState<ExpenseResponseDto | null>(null);

  // ==================== Query Hooks ====================
  const { data: expenses } = useExpenses(groupId, session.id);
  const { data: balances } = useBalances(groupId, session.id);
  const { data: sessionImages } = useSessionImages(groupId, session.id);

  // ==================== Mutation Hooks ====================
  const createExpenseMutation = useCreateExpense(groupId, session.id);
  const deleteExpenseMutation = useDeleteExpense(groupId, session.id);
  const startSettlingMutation = useStartSettling(groupId);
  const completeMutation = useCompleteSession(groupId);
  const reopenMutation = useReopenSession(groupId);
  const addImageMutation = useAddSessionImage(groupId, session.id);
  const removeImageMutation = useRemoveSessionImage(groupId, session.id);
  const splitExpenseMutation = useSplitExpenseEqually(groupId, session.id);

  // Update expense mutation - only created when editing
  const updateExpenseMutation = useUpdateExpense(groupId, session.id, editingExpense?.id ?? "");

  // ==================== Computed Values ====================
  const isInputting = session.status === "inputting";
  const isSettling = session.status === "settling";
  const isCompleted = session.status === "completed";
  // Only allow expense modifications during inputting phase
  const canModify = canEdit && isInputting;

  // ==================== Handlers ====================
  const handleStartSettling = useCallback(async () => {
    try {
      await startSettlingMutation.mutateAsync(session.id);
      toast.success(t("sessions.startSettlingSuccess", "入力を完了しました。精算を開始します。"));
    } catch {
      toast.error(t("sessions.startSettlingError", "入力完了に失敗しました"));
    }
  }, [startSettlingMutation, session.id, t]);

  const handleComplete = useCallback(async () => {
    try {
      await completeMutation.mutateAsync(session.id);
      toast.success(t("sessions.completeSuccess", "精算を完了しました"));
    } catch {
      toast.error(t("sessions.completeError", "完了に失敗しました"));
    }
  }, [completeMutation, session.id, t]);

  const handleReopen = useCallback(async () => {
    try {
      await reopenMutation.mutateAsync(session.id);
      toast.success(t("sessions.reopenSuccess", "精算を再開しました"));
    } catch {
      toast.error(t("sessions.reopenError", "再開に失敗しました"));
    }
  }, [reopenMutation, session.id, t]);

  const handleEditExpense = useCallback((expense: ExpenseResponseDto) => {
    setEditingExpense(expense);
    setShowExpenseDialog(true);
  }, []);

  const handleDeleteExpense = useCallback(
    async (expenseId: string) => {
      const confirmed = await confirm({
        title: t("expenses.deleteTitle"),
        description: t("expenses.deleteConfirm"),
        confirmText: t("common.delete"),
        cancelText: t("common.cancel"),
        destructive: true,
      });
      if (confirmed) {
        deleteExpenseMutation.mutate(expenseId);
      }
    },
    [confirm, t, deleteExpenseMutation]
  );

  const handleSplitAll = useCallback(
    async (expense: ExpenseResponseDto) => {
      try {
        await splitExpenseMutation.mutateAsync({
          expense,
          members: members.map((m) => ({
            memberId: m.memberId,
            memberType: m.memberType,
            joinedAt: m.joinedAt,
          })),
        });
        toast.success(t("expenses.splitAllSuccess", "全員で割り勘に変更しました"));
      } catch {
        toast.error(t("expenses.splitAllError", "変更に失敗しました"));
      }
    },
    [splitExpenseMutation, members, t]
  );

  const handleCloseExpenseDialog = useCallback((open: boolean) => {
    setShowExpenseDialog(open);
    if (!open) {
      setEditingExpense(null);
    }
  }, []);

  const handleShowExpenseDialog = useCallback(() => {
    setShowExpenseDialog(true);
  }, []);

  const handleSubmitExpense = useCallback(
    (expense: unknown) => {
      if (editingExpense) {
        // Update existing expense
        updateExpenseMutation.mutate(
          expense as Parameters<typeof updateExpenseMutation.mutate>[0],
          {
            onSuccess: () => {
              setShowExpenseDialog(false);
              setEditingExpense(null);
              toast.success(t("expenses.updateSuccess", "出費を更新しました"));
            },
          }
        );
      } else {
        // Create new expense
        createExpenseMutation.mutate(
          expense as Parameters<typeof createExpenseMutation.mutate>[0],
          {
            onSuccess: () => setShowExpenseDialog(false),
          }
        );
      }
    },
    [editingExpense, updateExpenseMutation, createExpenseMutation, t]
  );

  // ==================== Render ====================
  return (
    <>
      <SessionDetailPresenter
        groupId={groupId}
        session={session}
        members={members}
        currentUserId={currentUserId}
        currentUserType={currentUserType}
        canEdit={canEdit}
        simplified={simplified}
        expenses={expenses ?? []}
        balances={balances ?? []}
        sessionImages={sessionImages ?? []}
        showExpenseDialog={showExpenseDialog}
        editingExpense={editingExpense}
        isInputting={isInputting}
        isSettling={isSettling}
        isCompleted={isCompleted}
        canModify={canModify}
        isStartingSettling={startSettlingMutation.isPending}
        isCompleting={completeMutation.isPending}
        isReopening={reopenMutation.isPending}
        isUploadingImage={addImageMutation.isPending}
        isSubmittingExpense={
          editingExpense ? updateExpenseMutation.isPending : createExpenseMutation.isPending
        }
        onBack={onBack}
        onStartSettling={handleStartSettling}
        onComplete={handleComplete}
        onReopen={handleReopen}
        onEditExpense={handleEditExpense}
        onDeleteExpense={handleDeleteExpense}
        onSplitAll={handleSplitAll}
        onUploadImage={addImageMutation.mutateAsync}
        onRemoveImage={removeImageMutation.mutateAsync}
        onShowExpenseDialog={handleShowExpenseDialog}
        onCloseExpenseDialog={handleCloseExpenseDialog}
        onSubmitExpense={handleSubmitExpense}
      />
      <ConfirmDialog {...dialogProps} />
    </>
  );
}
